import React, {useEffect, useState} from 'react';
import {Button, CaretIcon} from '@/web-components';
import {FakeHeaderContainer, GoBackContainer, GoBackText} from '../salesforce/app_salesforce_style';
import CustomScrollDiv from '../../../shared/components/scroll-bars/custom_scroll_div';
import {FormattedMessage, useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import {createFragmentContainer} from 'react-relay';
import {ButtonsWrapper, DescriptionWrapper, IconAndDescriptionWrapper} from '../hubspot/app_hubspot_style';
import {
	MainSectionWrapper,
	PageWrapper,
	SubSection,
} from '../../../project-tab/projects/project-settings/ProjectSettings_styled';
import Util from '../../../shared/util/util';
import CreateDocusignVerificationKeyMutation from '../../../../mutations/CreateDocusignVerificationKeyMutation';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import Warning from '../../../../components/warning';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../../constants';
import DisableDocusignMutation from '../../../../mutations/DisableDocusignMutation';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import styled from 'styled-components';

const DocusignIconPlaceholder = styled.div`
	width: 100px;
	height: 100px;
	background-color: #003087;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-weight: bold;
	font-size: 24px;
`;

const AppDocusign = ({history, viewer}) => {
	const intl = useIntl();

	const [loading, setLoading] = useState(false);

	const openHelpCenter = () => {
		window.open('https://support.forecast.app/hc/en-us');
	};

	const goBackToIntegrationsPage = () => {
		history.push('/admin/integrations');
	};

	const activateDocusign = () => {
		setLoading(true);

		Util.CommitMutation(CreateDocusignVerificationKeyMutation, null, ({createDocusignVerificationKey}) => {
			const clientId = process.env.DOCUSIGN_APP_CLIENT_ID;
			const redirectUri = process.env.DOCUSIGN_REDIRECT_URI;
			const scopes = ['signature', 'impersonation'];
			const state = `forecast_${atob(viewer.company.id).split(':')[1]}_${createDocusignVerificationKey.key}`;

			window.open(
				`https://account-d.docusign.com/oauth/auth?response_type=code&scope=${scopes.join(
					'%20'
				)}&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}`,
				'_self'
			);
			setLoading(false);
		});
	};

	useEffect(() => {
		const urlSearchParams = new URLSearchParams(window.location.search);
		const installDocusign = urlSearchParams.get('install');
		if (installDocusign) {
			activateDocusign();
		}
	}, []);

	const deactivateIntegration = () => {
		const {formatMessage} = intl;

		const callbackPositive = () => {
			setLoading(true);
			Util.CommitMutation(DisableDocusignMutation, null, () => setLoading(false));
		};

		const info = 'settings.app_catalog.docusign.disable_message_warning';

		showModal({
			type: MODAL_TYPE.GENERIC,
			content: (
				<div>
					<Warning messageId={info} />
					<div className="warning-part-2">{formatMessage({id: 'common.warning.this_action_can_not_be_undone'})}</div>
				</div>
			),
			className: 'default-warning-modal',
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.delete'}),
					callback: callbackPositive,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
				},
			],
		});
	};

	const docusignEnabled = viewer.company.docusignEnabled;

	const actionButton = docusignEnabled ? (
		<Button variant={Button.VARIANT.RED_OUTLINE} onClick={deactivateIntegration}>
			<FormattedMessage id="common.deactivate" />
		</Button>
	) : (
		<Button variant={Button.VARIANT.PURPLE_OUTLINE} onClick={activateDocusign}>
			<FormattedMessage id="common.activate" />
		</Button>
	);
	return (
		<PageWrapper>
			<MainSectionWrapper width={1250}>
				<FakeHeaderContainer onClick={goBackToIntegrationsPage}>
					<GoBackContainer>
						<CaretIcon size={CaretIcon.SIZE.STANDARD} color={'#535353'} direction={CaretIcon.DIRECTION.LEFT} />
						<GoBackText>
							{intl.formatMessage({id: 'app_salesforce.salesforce_configuration.go_back_to_integrations'})}
						</GoBackText>
					</GoBackContainer>
				</FakeHeaderContainer>

				<CustomScrollDiv>
					<SubSection>
						<IconAndDescriptionWrapper>
							<DocusignIconPlaceholder>DS</DocusignIconPlaceholder>
							<DescriptionWrapper>
								<FormattedMessage id="settings.app_catalog.docusign.description" />
							</DescriptionWrapper>
						</IconAndDescriptionWrapper>

						<ButtonsWrapper>
							{actionButton}
							<Button variant={Button.VARIANT.STANDARD} onClick={openHelpCenter}>
								<FormattedMessage id="settings.app_catalog.help_button" />
							</Button>
						</ButtonsWrapper>
					</SubSection>

					{docusignEnabled && !loading && <>Docusign is enabled</>}

					{loading && <InlineLoader loaderText={intl.formatMessage({id: 'common.loading'})} />}
				</CustomScrollDiv>
			</MainSectionWrapper>
		</PageWrapper>
	);
};

const appDocusignQuery = graphql`
	query AppDocusign_Query {
		viewer {
			actualPersonId
			component(name: "app_docusign")
			...AppDocusign_viewer
		}
	}
`;

export {appDocusignQuery};

export default withRouter(
	createFragmentContainer(AppDocusign, {
		viewer: graphql`
			fragment AppDocusign_viewer on Viewer {
				id
				company {
					id
					docusignEnabled
				}
			}
		`,
	})
);
